/* ============================================= */
/*       ChatControlStack.jsx - Enhanced Controls   */
/* ============================================= */

import React, { useState, useEffect } from 'react';
import { NotebookPen, Sparkles, Lightbulb, X } from 'lucide-react';
import { Tooltip } from 'react-tooltip';
import { useConversation } from '@/managers/ConvoManager';
import NextMessageOptionsModal from './NextMessageOptionsModal';
import { authManager } from '@/managers/AuthManager';

const tooltipStyles = {
    backgroundColor: 'var(--surface-darker)', color: 'var(--on-surface)', fontSize: '0.75rem',
    padding: '0.5rem 0.75rem', borderRadius: '0.375rem', zIndex: 5000,
    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
};
const tooltipBorder = '1px solid var(--border-color)';

const ChatControlStackComponent = () => {
    const { generateAndInsertSummary } = useConversation();

    const [isNextMessageOptionsModalOpen, setIsNextMessageOptionsModalOpen] = useState(false);
    const [showOnboarding, setShowOnboarding] = useState(false);

    useEffect(() => {
        const handleAuthState = (state) => {
            if (state.settingsLoaded) {
                setShowOnboarding(!state.settings.zPreferences.onboardingFinished);
            }
        };
        const authUnsubscribe = authManager.subscribe(handleAuthState);

        return () => {
            authUnsubscribe();
        };
    }, []);
    
    const anyModalOpen = isNextMessageOptionsModalOpen;

    const handleGenerate = async () => {
        if (typeof generateAndInsertSummary === 'function') {
            await generateAndInsertSummary();
        } else {
            console.error("Action 'generateAndInsertSummary' is not available from useConversation hook.");
        }
    };

    const handleDismissOnboarding = () => {
        setShowOnboarding(false);
        authManager.updateSettings({ zPreferences: { onboardingFinished: true } });
    };

    return (
        <div className="relative" style={{ pointerEvents: 'none' }}>

            {/* ================================================================== */}
            {/* == ONBOARDING BUBBLE - Pixel-perfect replica of the image provided == */}
            {/* ================================================================== */}
            {showOnboarding && (
                <div
                    className="absolute bottom-full right-0 mb-4 w-[280px] z-[1000] rounded-lg bg-background"
                    style={{ pointerEvents: 'auto' }}
                >
                    <div className="relative drop-shadow-xl">
                        {/* Bubble Body */}
                        <div
                            className="relative flex items-start gap-3 p-4 rounded-lg"
                            style={{
                                background: 'var(--surface-darker)',
                                border: '1px solid var(--border-color)',
                            }}
                        >
                            {/* Pointer (created with a rotated div) */}
                            <div
                                className="absolute w-3 h-3"
                                style={{
                                    background: 'var(--surface-darker)',
                                    borderRight: '1px solid var(--border-color)',
                                    borderBottom: '1px solid var(--border-color)',
                                    bottom: '-7px',
                                    right: '13px', // Centers on the ~26px wide Cog button
                                    transform: 'translateX(50%) rotate(45deg)',
                                }}
                            />
                            {/* Lightbulb Icon */}
                            <Lightbulb className="w-5 h-5 text-primary flex-shrink-0" style={{ color: 'var(--accent-warm)'}} />
                            
                            {/* Text Content */}
                            <p className="flex-1 text-sm text-on-surface leading-relaxed mt-px">
                                Switch bots or access tools and MCP servers from here.
                            </p>

                            {/* Dismiss Button */}
                            <button
                                onClick={handleDismissOnboarding}
                                className="-mt-1 -mr-1 p-1.5 text-on-surface-dimmest hover:text-on-surface rounded-full transition-colors flex-shrink-0"
                                aria-label="Dismiss"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Control Button Container */}
            <div className="flex flex-col gap-2" style={{ pointerEvents: 'auto' }}>
                <div className="flex justify-end gap-2">
                    <button
                        onClick={() => {
                            if (showOnboarding) handleDismissOnboarding();
                            setIsNextMessageOptionsModalOpen(prev => !prev);
                        }}
                        className="p-1.5 rounded-full hover:bg-surface-dark transition-colors duration-200 backdrop-blur-sm shadow-sm ring-1 ring-white/10"
                        data-tooltip-id="next-message-options-tooltip"
                        data-tooltip-content="Convo Toolkit / Switch Bot"
                        style={{ backgroundColor: 'rgba(var(--surface-rgb), 0.5)' }}
                    >
                        <Sparkles className="w-3.5 h-3.5 text-on-surface-dim" strokeWidth={1.5} />
                    </button>
                </div>
                <div className="relative flex justify-end gap-2">
                    <div className="relative">
                        <button
                            className="p-1.5 rounded-full hover:bg-surface-dark transition-colors duration-200 backdrop-blur-sm shadow-sm ring-1 ring-white/10"
                            data-tooltip-id="summary-tooltip"
                            data-tooltip-content="Generate Chat Summary"
                            onClick={handleGenerate}
                            style={{ backgroundColor: 'rgba(var(--surface-rgb), 0.5)' }}
                        >
                            <NotebookPen className="w-3.5 h-3.5 text-on-surface-dim" strokeWidth={1.5} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Tooltips */}
            <Tooltip id="next-message-options-tooltip" place="left" delayShow={500} style={tooltipStyles} border={tooltipBorder} noArrow hidden={anyModalOpen}/>
            <Tooltip id="summary-tooltip" place="left" delayShow={500} style={tooltipStyles} border={tooltipBorder} noArrow hidden={anyModalOpen}/>

            {/* Modals */}
            <NextMessageOptionsModal
                isOpen={isNextMessageOptionsModalOpen}
                onClose={() => setIsNextMessageOptionsModalOpen(false)}
            />
        </div>
    );
};

const ChatControlStack = React.memo(ChatControlStackComponent);

export default ChatControlStack;